import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { useSyncExternalStore } from 'react';

/**
 * The single state machine that controls what surface the user sees during
 * app boot. Derived purely from useAuthStore + useWorkspaceStore + the
 * module-level splash flag — no new source of truth, no internal state.
 *
 *   splash               splash animation still in flight OR auth resume
 *                        is still loading
 *   unauthenticated      splash done, auth resolved, no token → show <Login />
 *   workspace-pending    authenticated, /api/workspaces fetch hasn't
 *                        completed yet OR completed without picking an active
 *                        workspace → show a brief loader
 *   onboarding-required  authenticated, fetch completed, server returned
 *                        zero workspaces → show <OnboardingFlow />
 *   ready                authenticated AND bootstrapped AND active workspace
 *                        set → mount the full provider stack + shell
 *
 * The boot gate (<BootGate>) switches on this state and renders exactly one
 * branch. Every downstream consumer (Sidebar, PanelLayout, etc.) is
 * structurally inside the ready branch and so is guaranteed to find its
 * providers in the tree.
 *
 * See docs/1-projects/auth0-revert/boot-state-machine-design.md for the
 * full design rationale.
 */
export type BootState =
  | 'splash'
  | 'unauthenticated'
  | 'workspace-pending'
  | 'onboarding-required'
  | 'ready';

// Module-level flag — survives React remounts (e.g. logout in the same
// session) so the splash only plays once per page load, not after every
// logout/re-render. A Zustand-stored equivalent would survive page
// reloads too, which is wrong: we want splash on fresh pageload but NOT
// on logout-then-resignin.
let _splashShown = false;

/** Reset for testing only. */
export function _resetSplash(): void {
  _splashShown = false;
  notifySplashListeners();
}

/** Test helper: force splashShown to true without playing the animation. */
export function _forceSplashShown(): void {
  _splashShown = true;
  notifySplashListeners();
}

/**
 * Mark the splash animation complete. Called once from <AppSplash /> when
 * its animation timer fires. Idempotent.
 */
export function markSplashShown(): void {
  if (_splashShown) return;
  _splashShown = true;
  notifySplashListeners();
}

// Subscribe-on-mount so React re-derives when the flag flips. The flag is
// module-local; without explicit subscription a state change would not
// trigger a re-render in components reading useBootState().
const splashListeners = new Set<() => void>();
function notifySplashListeners(): void {
  splashListeners.forEach((fn) => fn());
}
function subscribeToSplash(fn: () => void): () => void {
  splashListeners.add(fn);
  return () => {
    splashListeners.delete(fn);
  };
}
function getSplashSnapshot(): boolean {
  return _splashShown;
}

export function useBootState(): BootState {
  const splashShown = useSyncExternalStore(subscribeToSplash, getSplashSnapshot);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const bootstrapped = useWorkspaceStore((s) => s.bootstrapped);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);

  if (!splashShown || isLoading) return 'splash';
  if (!isAuthenticated) return 'unauthenticated';
  if (!bootstrapped) return 'workspace-pending';
  if (activeWorkspaceId !== null) return 'ready';
  if (workspaces.length === 0) return 'onboarding-required';
  // Edge case: bootstrapped, workspaces non-empty, but no active selection.
  // Happens transiently while refreshFromServer is in the middle of picking
  // the default — treat as still pending until the selection lands.
  return 'workspace-pending';
}
